import {
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
  useRef,
} from "react";
import CustomModal from "../CustomModal";
import Row from "../../../components/Row";
import Column from "../../../components/Column";
import Text from "../../../components/Text";
import styled from "styled-components";
import { styles } from "../../../styles/themes/style";
import { useFeedsHandlers } from "../../../services/actions/feeds/useFeedsHandlers";
import { useFeedsStore } from "../../../stores/feeds/useFeedsStore";
import DropdownMenu from "../../../components/dropdown/DropdownMenu";
import DropdownItem from "../../../components/dropdown/DropdownItem";
import SvgFragment from "../../../fragments/SvgFragment";
import Checkbox from "../../../components/Checkbox";
import { IFeed } from "../../../interfaces/models/feed";
import { useConnectionsHandlers } from "../../../services/actions/connections/useConnectionsHandlers";
import { useExtensionRequest } from "../../../services/apis/extension/useExtensionRequest";
import { usePostsHandlers } from "../../../services/actions/posts/usePostsHandlers";
import { useConnectionsStore } from "../../../stores/connections/useConnectionsStore";
import { IConnection } from "../../../interfaces/models/connection";
import Button from "../../Button";
import ActionText from "../../ActionText";
import TooltipWrapper from "../../TooltipWrapper";
import LoadingSpinner from "../../LoadingSpinner";
import { useAppHandlers } from "../../../services/actions/app/useAppHandlers";

interface ICreateFeedModalProps {
  selectedConnectionsIds?: string[];
  selectedAllConnections?: boolean;
  disableExistingFeed?: boolean;
  onClose?: () => void;
  urlMode?: boolean;
  selectedFeedId?: string; // Pre-selected feed ID for URL mode
}

const CreateFeedModal: FunctionComponent<ICreateFeedModalProps> = ({
  selectedConnectionsIds = [],
  disableExistingFeed = false,
  onClose,
  urlMode = false,
  selectedFeedId,
}) => {
  const {
    bulkAddConnectionsToFeedHandler,
    createFeedHandler,
    getFeedsHandler,
  } = useFeedsHandlers();
  const { bulkUpdatePostTrackingStatusHandler } = useConnectionsHandlers();
  const { getProfilePosts } = useExtensionRequest();
  const { syncProfilePostsHandler } = usePostsHandlers();
  const { showErrorToast, hideModal } = useAppHandlers();
  const {
    store: { connections },
  } = useConnectionsStore();
  const {
    store: { feeds: globalFeeds },
  } = useFeedsStore();

  const [feeds, setFeeds] = useState<IFeed[]>([]);
  const [selectedFeedIds, setSelectedFeedIds] = useState<string[]>([]);
  const [isCreatingNew, setIsCreatingNew] = useState(true);
  const [newFeedName, setNewFeedName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [linkedInUrls, setLinkedInUrls] = useState<string>("");
  const {
    store: { numConnections = -1, maxNumConnections = 0 },
  } = useFeedsStore();
  const feedNameInputRef = useRef<HTMLInputElement>(null);
  const feedsFetchedRef = useRef<boolean>(false);

  // Function to check if all selected connections are already in a feed
  const isFeedDisabled = useCallback(
    (feed: IFeed) => {
      // If no connections are selected, no feed should be disabled
      if (selectedConnectionsIds.length === 0) return false;

      // Check if all selected connections are already in this feed
      const allConnectionsInFeed = selectedConnectionsIds.every(
        (connectionId) => feed.connections.includes(connectionId)
      );

      return allConnectionsInFeed;
    },
    [selectedConnectionsIds]
  );

  // Get selected feed name for display
  const selectedFeedName = isCreatingNew
    ? "Create new feed"
    : selectedFeedIds.length === 0
    ? "Choose existing feed"
    : selectedFeedIds.length === 1
    ? feeds.find((f) => f._id === selectedFeedIds[0])?.name ||
      "Choose existing feed"
    : `${selectedFeedIds.length} feeds selected`;

  // Use feeds from global store, but fetch them if store is empty and existing feeds are enabled
  useEffect(() => {
    if (globalFeeds.length > 0) {
      setFeeds(globalFeeds);
    } else if (!disableExistingFeed && !feedsFetchedRef.current) {
      // Only fetch feeds if existing feeds are not disabled, store is empty, and we haven't fetched yet
      feedsFetchedRef.current = true;
      getFeedsHandler();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalFeeds, disableExistingFeed]);

  // Focus input field when modal opens
  useEffect(() => {
    if (isCreatingNew && feedNameInputRef.current) {
      // Small delay to ensure modal is fully rendered
      setTimeout(() => {
        feedNameInputRef.current?.focus();
      }, 100);
    }
  }, [isCreatingNew]);

  // Set isCreatingNew and selectedFeedIds based on props
  useEffect(() => {
    if (disableExistingFeed) {
      setIsCreatingNew(true);
    } else if (urlMode && selectedFeedId) {
      // For URL mode with pre-selected feed, set to "Add to existing feed" mode
      setIsCreatingNew(false);
      setSelectedFeedIds([selectedFeedId]);
    } else if (urlMode && !selectedFeedId) {
      // For URL mode without pre-selected feed, default to "Create new feed"
      setIsCreatingNew(true);
    }
  }, [disableExistingFeed, urlMode, selectedFeedId]);

  const handleFeedSelect = useCallback((feedId: string) => {
    setSelectedFeedIds((prev) => {
      if (prev.includes(feedId)) {
        // Remove from selection
        return prev.filter((id) => id !== feedId);
      } else {
        // Add to selection
        return [...prev, feedId];
      }
    });
  }, []);

  const handleToggleMode = useCallback(
    (creatingNew: boolean) => {
      if (disableExistingFeed) return; // Prevent toggle when existing feed is disabled
      setIsCreatingNew(creatingNew);
      if (creatingNew) {
        setSelectedFeedIds([]);
        setNewFeedName("");
        // Focus input field when switching to create new mode
        setTimeout(() => {
          feedNameInputRef.current?.focus();
        }, 100);
      } else {
        setNewFeedName("");
      }
      setIsDropdownOpen(false);
    },
    [disableExistingFeed]
  );

  // Helper function to validate and extract publicIdentifiers from LinkedIn URLs
  const extractPublicIdentifiersFromUrls = useCallback(
    (urls: string[]): string[] | null => {
      const linkedInUrlPattern =
        /^https?:\/\/(www\.)?linkedin\.com\/in\/([^/\s]+)\/?$/i;
      const invalidUrls: string[] = [];
      const validUrls: string[] = [];

      urls.forEach((url) => {
        if (linkedInUrlPattern.test(url)) {
          validUrls.push(url);
        } else {
          invalidUrls.push(url);
        }
      });

      if (invalidUrls.length > 0) {
        showErrorToast({
          message: `Invalid LinkedIn URL format. Please use format: https://www.linkedin.com/in/profile-name/`,
        });
        return null;
      }

      const publicIdentifiers = validUrls
        .map((url) => {
          const match = url.match(/\/in\/([^/\s]+)\/?$/i);
          if (match && match[1]) {
            return decodeURIComponent(match[1]);
          }
          return null;
        })
        .filter((id): id is string => id !== null);

      if (publicIdentifiers.length === 0) {
        showErrorToast({
          message: `Could not extract profile identifiers from URLs`,
        });
        return null;
      }

      return publicIdentifiers;
    },
    [showErrorToast]
  );

  // Helper function to fetch posts for connections
  const fetchPostsForConnections = useCallback(
    async (connectionsToFetch: IConnection[]) => {
      for (const connection of connectionsToFetch) {
        try {
          // Extract profileId and publicIdentifier from connection or nested profile object
          const profileId =
            connection.profileId || connection.profile?.profileId;
          const publicIdentifier =
            connection.publicIdentifier || connection.profile?.publicIdentifier;

          if (!profileId || !publicIdentifier) {
            console.log(
              "Skipping connection - missing profileId or publicIdentifier:",
              {
                connectionId: connection._id,
                hasProfileId: !!profileId,
                hasPublicIdentifier: !!publicIdentifier,
                profileIdValue: profileId,
                publicIdentifierValue: publicIdentifier,
              }
            );
            continue;
          }

          const profileUrn = `urn:li:fsd_profile:${profileId}`;
          const profilePosts = await getProfilePosts(profileUrn);

          if (profilePosts) {
            await syncProfilePostsHandler(profilePosts, publicIdentifier);
          }
        } catch (error) {
          console.error(
            `Error fetching posts for connection ${connection._id}:`,
            error
          );
        }
      }

      // Trigger refresh of posts feed if user is on posts page
      // Dispatch custom event that PostsFeed can listen to
      window.dispatchEvent(new CustomEvent('refreshPostsFeed'));
    },
    [getProfilePosts, syncProfilePostsHandler]
  );

  // Helper function to process result and fetch posts
  const processResultAndFetchPosts = useCallback(
    async (result: any, isUrlMode: boolean) => {
      if (!result?.success) {
        return;
      }

      // Determine which connection IDs and connections array to use
      let connectionIdsToUse: string[] = [];
      let connectionsToUse: IConnection[] = [];

      if (isUrlMode) {
        // Backend now returns all workspace connections created via URLs
        connectionIdsToUse =
          result?.data?.feed?.connections || result?.data?.workspaceConnections?.map((c: IConnection) => c._id) || [];

        connectionsToUse =
          result?.data?.workspaceConnections && result.data.workspaceConnections.length > 0
            ? result.data.workspaceConnections
            : connections.filter((conn: IConnection) => connectionIdsToUse.includes(conn._id));
      } else {
        // Normal mode - use selectedConnectionsIds
        connectionIdsToUse = selectedConnectionsIds;
        connectionsToUse = connections;
      }

      console.log("Processing result:", {
        isUrlMode,
        connectionIdsToUse,
        connectionsToUseCount: connectionsToUse.length,
        addedCount: result?.data?.addedCount,
        feedConnections: result?.data?.feed?.connections,
        newWorkspaceConnectionIds: result?.data?.newWorkspaceConnectionIds,
      });

      // Update tracking status
      if (connectionIdsToUse.length > 0) {
        await bulkUpdatePostTrackingStatusHandler(connectionIdsToUse, true);
      }

      // Fetch posts if we have connections to process
      // For URL mode, check workspaceConnections or addedCount
      // For normal mode, check addedCount
      const shouldFetchPosts = isUrlMode
        ? (connectionsToUse.length > 0 && (result?.data?.workspaceConnections?.length > 0 || result?.data?.addedCount > 0))
        : (result?.data?.addedCount > 0 && connectionsToUse.length > 0);
      
      if (shouldFetchPosts) {
        const connectionsToFetchPosts = connectionsToUse.filter(
          (conn: IConnection) => {
            // Extract profileId and publicIdentifier from connection or nested profile object
            const profileId = conn.profileId || conn.profile?.profileId;
            const publicIdentifier =
              conn.publicIdentifier || conn.profile?.publicIdentifier;
            return (
              connectionIdsToUse.includes(conn._id) &&
              profileId &&
              publicIdentifier
            );
          }
        );

        console.log("Connections to fetch posts for:", {
          totalConnections: connectionsToUse.length,
          connectionsToFetchCount: connectionsToFetchPosts.length,
          connectionIdsToUse: connectionIdsToUse.length,
          connections: connectionsToFetchPosts.map((c: IConnection) => ({
            id: c._id,
            profileId: c.profileId || c.profile?.profileId,
            publicIdentifier: c.publicIdentifier || c.profile?.publicIdentifier,
          })),
        });

        if (connectionsToFetchPosts.length > 0) {
          await fetchPostsForConnections(connectionsToFetchPosts);
          // After all syncProfilePostsHandler calls complete, trigger refresh
          // Loading will be stopped after getWorkspacePostsHandler completes in PostsFeed
        } else {
          // If no connections to fetch posts for, stop loading indicator
          window.dispatchEvent(new CustomEvent('stopLoadingPostsAfterAdd'));
        }
      } else {
        // If we shouldn't fetch posts, stop loading indicator
        window.dispatchEvent(new CustomEvent('stopLoadingPostsAfterAdd'));
      }
    },
    [
      selectedConnectionsIds,
      connections,
      bulkUpdatePostTrackingStatusHandler,
      fetchPostsForConnections,
    ]
  );

  const handleSubmit = useCallback(async () => {
    // Validation
    if (isCreatingNew && !newFeedName.trim()) {
      return;
    }

    setSubmitting(true);
    try {
      let result;
      let publicIdentifiers: string[] | null = null;

      // Handle URL mode - validate and extract publicIdentifiers
      if (urlMode) {
        const urls = linkedInUrls
          .split("\n")
          .map((url) => url.trim())
          .filter((url) => url.length > 0);

        publicIdentifiers = extractPublicIdentifiersFromUrls(urls);
        if (!publicIdentifiers) {
          setSubmitting(false);
          return;
        }
      }

      // Execute feed operation based on mode
      if (disableExistingFeed) {
        // Create feed with connections (normal mode only)
        result = await createFeedHandler(
          newFeedName.trim(),
          selectedConnectionsIds
        );
      } else if (isCreatingNew) {
        // Create new feed
        if (urlMode && publicIdentifiers) {
          // Create feed with publicIdentifiers from URLs
          result = await createFeedHandler(
            newFeedName.trim(),
            undefined, // connectionIds - empty when using publicIdentifiers
            publicIdentifiers
          );
        } else {
          result = await createFeedHandler(
            newFeedName.trim(),
            selectedConnectionsIds
          );
        }
      } else {
        // Add to existing feed
        if (urlMode && publicIdentifiers) {
          result = await bulkAddConnectionsToFeedHandler(
            [], // connectionIds - empty when using publicIdentifiers
            selectedFeedIds,
            undefined,
            publicIdentifiers
          );
        } else {
          result = await bulkAddConnectionsToFeedHandler(
            selectedConnectionsIds,
            selectedFeedIds,
            undefined
          );
        }
      }

      // Process result and fetch posts for create + add (Connections and URL).
      // createFeedHandler already closes the modal; bulkAdd needs explicit close below.
      if (result && result.success) {
        const hasConnectionsToFetch = urlMode && isCreatingNew
          ? (result?.data?.workspaceConnections?.length > 0 || result?.data?.addedCount > 0)
          : result?.data?.addedCount > 0;

        if (hasConnectionsToFetch) {
          window.dispatchEvent(new CustomEvent('startLoadingPostsAfterAdd'));
        }

        await processResultAndFetchPosts(result, urlMode);

        if (!isCreatingNew) {
          const hasNotFoundIdentifiers = urlMode && result?.data?.notFoundIdentifiers && result.data.notFoundIdentifiers.length > 0;
          if (!hasNotFoundIdentifiers) {
            if (onClose) {
              onClose();
            } else {
              hideModal();
            }
          }
        }
      }
    } finally {
      setSubmitting(false);
    }
  }, [
    isCreatingNew,
    newFeedName,
    selectedFeedIds,
    selectedConnectionsIds,
    disableExistingFeed,
    urlMode,
    linkedInUrls,
    extractPublicIdentifiersFromUrls,
    processResultAndFetchPosts,
    bulkAddConnectionsToFeedHandler,
    createFeedHandler,
    onClose,
    hideModal,
  ]);

  // Check if feed limit is reached
  const isLimitReached = numConnections >= maxNumConnections;

  const canSubmit =
    !isLimitReached &&
    (urlMode
      ? isCreatingNew
        ? newFeedName.trim().length > 0 && linkedInUrls.trim().length > 0
        : selectedFeedIds.length > 0 && linkedInUrls.trim().length > 0
      : isCreatingNew
      ? newFeedName.trim().length > 0
      : selectedFeedIds.length > 0);

  // Handle keyboard events
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        if (isDropdownOpen) {
          // When dropdown is open, let the dropdown handle Enter
          // Don't prevent default or call handleSubmit
          return;
        } else if (canSubmit && !submitting) {
          // When dropdown is closed, Enter should submit
          e.preventDefault();
          handleSubmit();
        }
      } else if (e.key === "Escape") {
        e.preventDefault();
        if (isDropdownOpen) {
          // Close dropdown if it's open
          setIsDropdownOpen(false);
        } else {
          // Close modal if dropdown is closed
          onClose?.();
        }
      }
    },
    [canSubmit, submitting, handleSubmit, isDropdownOpen, onClose]
  );

  const selectedFeedButton = (
    <Container 
      $hasValue={!isCreatingNew && selectedFeedIds.length > 0}
      $disabled={isLimitReached || !!selectedFeedId}
    >
      {selectedFeedName}
    </Container>
  );

  return (
    <CustomModal
      headingText={
        urlMode ? "Create custom feed from URLs" : "Create custom feed"
      }
      closeButton
      bodyPadding="0px 24px 8px"
    >
      <Column gap="16px" onKeyDown={handleKeyDown} tabIndex={-1}>
        {!urlMode && (
          <Text>
            Monitor posts from specific LinkedIn contacts. Add people to a new
            feed or include them in an existing one.
          </Text>
        )}

        <NoteBox $warning={isLimitReached}>
          <StyledText $description>
            {maxNumConnections === 0 ? (
              <Row
                gap="8px"
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <LoadingSpinner />
              </Row>
            ) : isLimitReached ? (
              `Maximum of ${maxNumConnections} contacts added to feeds. Remove some contacts to add new ones. `
            ) : (
              `Note: You can add up to ${maxNumConnections} contacts total for all your feeds.`
            )}
            {isLimitReached && maxNumConnections > 0 && (
              <ActionText
                onClickHandler={() => (window.location.href = "/posts")}
                textDecoration="underline"
              >
                Open Custom Feed
              </ActionText>
            )}
          </StyledText>
        </NoteBox>

        {!disableExistingFeed && !(urlMode && selectedFeedId) && (
          <ToggleContainer>
            <ToggleButton
              $active={isCreatingNew}
              $disabled={isLimitReached}
              onClick={() => !isLimitReached && handleToggleMode(true)}
            >
              <SvgFragment type="plus" />
              <Text>Create new feed</Text>
            </ToggleButton>
            <ToggleButton
              $active={!isCreatingNew}
              $disabled={isLimitReached}
              onClick={() => !isLimitReached && handleToggleMode(false)}
            >
              <SvgFragment type="plus" />
              <Text>Add to existing feed</Text>
            </ToggleButton>
          </ToggleContainer>
        )}

        {!isCreatingNew && !urlMode && (
          <Column gap="8px">
            <StyledDropdownMenu
              selectStyle
              tooltipMessage={
                isLimitReached ? "Feed limit reached" : "Choose existing feed"
              }
              title={selectedFeedButton}
              show={isDropdownOpen && !isLimitReached}
              onToggle={!isLimitReached ? setIsDropdownOpen : undefined}
              disabled={isLimitReached}
            >
              {feeds && feeds.length > 0 ? (
                feeds.map((feed) => {
                  const isDisabled = isFeedDisabled(feed);

                  const dropdownItemContent = (
                    <Row alignItems spaceBetween>
                      <Row alignItems gap="8px">
                        <Checkbox
                          checked={selectedFeedIds.includes(feed._id)}
                          checkboxId={feed._id}
                          onClickHandler={(e) => {
                            e.stopPropagation();
                            if (!isDisabled) {
                              handleFeedSelect(feed._id);
                            }
                          }}
                          $height="16px"
                          disabled={isDisabled}
                        />
                        <Text $faded={isDisabled}>{feed.name}</Text>
                      </Row>
                      <Text $description $faded>
                        {feed.numConnections} contacts
                        {isDisabled && " (All selected)"}
                      </Text>
                    </Row>
                  );

                  return (
                    <div
                      key={feed._id}
                      style={{
                        cursor: isDisabled ? "default" : "pointer",
                        opacity: isDisabled ? 0.6 : 1,
                      }}
                    >
                      <DropdownItem
                        onClickHandler={(e) => {
                          e.stopPropagation();
                          if (!isDisabled) {
                            handleFeedSelect(feed._id);
                          }
                        }}
                        $disabled={false}
                      >
                        {isDisabled ? (
                          <TooltipWrapper
                            tooltipText="Contact(s) already added to the feed"
                            placement="top"
                          >
                            <div>{dropdownItemContent}</div>
                          </TooltipWrapper>
                        ) : (
                          dropdownItemContent
                        )}
                      </DropdownItem>
                    </div>
                  );
                })
              ) : (
                <EmptyFeedsContainer>
                  <Text $description $faded>
                    No feeds created yet
                  </Text>
                </EmptyFeedsContainer>
              )}
            </StyledDropdownMenu>
          </Column>
        )}

        {isCreatingNew && (
          <Column gap="8px">
            <Text $heading6>FEED NAME</Text>
            <FeedNameInput
              ref={feedNameInputRef}
              type="text"
              placeholder={
                isLimitReached ? "Feed limit reached" : "Type name..."
              }
              value={newFeedName}
              onChange={(e) =>
                !isLimitReached && setNewFeedName(e.target.value)
              }
              maxLength={50}
              disabled={isLimitReached}
            />
          </Column>
        )}

        {urlMode && !isCreatingNew && (
          <Column gap="8px">
            <StyledDropdownMenu
              selectStyle
              tooltipMessage={
                isLimitReached ? "Feed limit reached" : selectedFeedId ? selectedFeedName : "Choose existing feed"
              }
              title={selectedFeedButton}
              show={isDropdownOpen && !isLimitReached && !selectedFeedId}
              onToggle={!isLimitReached && !selectedFeedId ? setIsDropdownOpen : undefined}
              disabled={isLimitReached || !!selectedFeedId}
            >
              {feeds && feeds.length > 0 ? (
                feeds.map((feed) => {
                  const dropdownItemContent = (
                    <Row alignItems spaceBetween>
                      <Row alignItems gap="8px">
                        <Checkbox
                          checked={selectedFeedIds.includes(feed._id)}
                          checkboxId={feed._id}
                          onClickHandler={(e) => {
                            e.stopPropagation();
                            handleFeedSelect(feed._id);
                          }}
                          $height="16px"
                        />
                        <Text>{feed.name}</Text>
                      </Row>
                      <Text $description $faded>
                        {feed.numConnections} contacts
                      </Text>
                    </Row>
                  );

                  return (
                    <div key={feed._id}>
                      <DropdownItem
                        onClickHandler={(e) => {
                          e.stopPropagation();
                          handleFeedSelect(feed._id);
                        }}
                        $disabled={false}
                      >
                        {dropdownItemContent}
                      </DropdownItem>
                    </div>
                  );
                })
              ) : (
                <EmptyFeedsContainer>
                  <Text $description $faded>
                    No feeds created yet
                  </Text>
                </EmptyFeedsContainer>
              )}
            </StyledDropdownMenu>
          </Column>
        )}
        {urlMode && (
          <Column gap="8px">
            <Text $heading6>LINKEDIN PROFILE URLS</Text>
            <LinkedInUrlsTextarea
              placeholder="http://www.linkedin.com/in/profile-1/
http://www.linkedin.com/in/profile-2/
http://www.linkedin.com/in/profile-3/
http://www.linkedin.com/in/profile-4/
http://www.linkedin.com/in/profile-5/
http://www.linkedin.com/in/profile-6/
http://www.linkedin.com/in/profile-7/
http://www.linkedin.com/in/profile-8/"
              value={linkedInUrls}
              onChange={(e) => setLinkedInUrls(e.target.value)}
              onKeyDown={(e) => {
                // Allow Enter to create new line in textarea
                if (e.key === "Enter") {
                  e.stopPropagation();
                  // Don't prevent default - let Enter create new line
                }
              }}
              rows={8}
              disabled={isLimitReached}
            />
          </Column>
        )}


        <Row spaceBetween style={{ flexDirection: "row-reverse" }}>
          <Button
            onClickHandler={handleSubmit}
            disabled={!canSubmit || submitting}
            $type="blue"
            isLoading={submitting}
            $width={isCreatingNew ? "122px" : "auto"}
            $customDisabled={styles.colors.primary200}
          >
            {submitting
              ? isCreatingNew
                ? "Creating..."
                : "Adding..."
              : isCreatingNew
              ? "Create new feed"
              : "Add to feed"}
          </Button>
        </Row>
      </Column>
    </CustomModal>
  );
};

const NoteBox = styled.div<{ $warning?: boolean }>`
  padding: 12px 16px;
  background: ${({ $warning }) =>
    $warning ? styles.colors.yellow200 : "#f8f9fa"};
  border-left: 3px solid
    ${({ $warning }) =>
      $warning ? styles.colors.yellow600 : styles.colors.primary600};
  border-radius: 4px;
`;

const StyledText = styled(Text)`
  display: inline;
  text-align: left;

  & > * {
    display: inline;
  }
`;

const FeedNameInput = styled.input`
  width: 432px;
  height: 34px;
  padding: 0 12px;
  border: 2px solid #dae0e8;
  border-radius: 4px;
  background: #ffffff;
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s ease;
  opacity: 1;

  &:focus {
    border-color: ${styles.colors.primary600};
  }

  &::placeholder {
    color: #999;
  }

  &:disabled {
    background: #f5f5f5;
    border-color: #ddd;
    cursor: not-allowed;
    opacity: 0.7;

    &::placeholder {
      color: #bbb;
    }
  }
`;

const LinkedInUrlsTextarea = styled.textarea`
  width: 432px;
  min-height: 180px;
  padding: 7px 10px;
  border: 2px solid #dae0e8;
  border-radius: 4px;
  background: #ffffff;
  font-size: 14px;
  font-family: NunitoSans;
  outline: none;
  transition: border-color 0.2s ease;
  resize: vertical;
  line-height: 20px;

  &:focus {
    border-color: ${styles.colors.primary600};
  }

  &::placeholder {
    color: #999;
  }

  &:disabled {
    background: #f5f5f5;
    border-color: #ddd;
    cursor: not-allowed;
    opacity: 0.7;

    &::placeholder {
      color: #bbb;
    }
  }
`;

const Container = styled.div<{ $hasValue?: boolean; $disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  height: 34px;
  border: none;
  width: 100%;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  line-height: 20px;
  font-weight: bold;
  color: ${({ $hasValue, $disabled }) => 
    $disabled ? styles.colors.disabledState : 
    $hasValue ? '#333951' : styles.colors.disabledState}
`;

const ToggleContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  background: #eef3ff;
  padding: 4px;
  border-radius: 4px;
  height: 42px;
`;

const ToggleButton = styled.button<{ $active: boolean; $disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 210px;
  height: 34px;
  padding: 9px 8px;
  border-radius: 4px;
  border: ${({ $active }) =>
    $active ? `1px solid ${styles.colors.primary600}` : "none"};
  background: ${({ $active }) => ($active ? "#FFFFFF" : "transparent")};
  color: ${({ $disabled }) =>
    $disabled ? styles.colors.black300 : styles.colors.primary600};
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};

  &:hover {
    background: ${({ $active, $disabled }) =>
      $disabled
        ? "transparent"
        : $active
        ? "#FFFFFF"
        : "rgba(255, 255, 255, 0.5)"};
  }

  svg path {
    fill: ${({ $disabled }) =>
      $disabled ? styles.colors.black300 : styles.colors.primary600};
  }

  span {
    color: ${({ $disabled }) =>
      $disabled ? styles.colors.black300 : styles.colors.primary600};
    font-family: NunitoSans;
    font-weight: 700;
    font-style: Bold;
    font-size: 14px;
    leading-trim: NONE;
    line-height: 20px;
    letter-spacing: 0px;
  }
`;

const EmptyFeedsContainer = styled.div`
  padding: 16px 12px;
  text-align: center;
  color: ${styles.colors.black400};
`;

const StyledDropdownMenu = styled(DropdownMenu)`
  border: ${({ border }) => (border ? `${border}` : "none")};
  .actions-dropdown + div {
    width: ${({ panelWidth }) => (panelWidth ? `${panelWidth}` : "100%")};
    max-height: 350px;
    overflow: hidden !important;
  }

  .plus-svg path {
    fill: ${styles.colors.primary600};
  }
`;

export default CreateFeedModal;
